import Button from '@mui/material/Button/Button';
import React, { useCallback, useState } from 'react';

import { DividerWithText } from '@components/shared';
import { decodeToken } from '@config/router/guards';
import { AuthHeader } from '@features/auth/AuthHeader';
import { StepNames } from '@features/auth/registration/registration.types';
import { AuthPepper } from '@features/AuthPaper';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useGoogleLogin } from '@react-oauth/google';
import { RoleEnum, SpecialistType } from '@utils/util.types';
import { RoleCard } from 'components/RoleCard';
import { useTranslation } from 'react-i18next';
import { useLoginWithGoogle, useRegisterWithGoogle } from 'services/auth';
import { GoogleButton } from './GoogleButton';
import { generalRegistrationRoles } from './rolesSelectionData';

export const RoleSelection: React.FC = () => {
	const { t } = useTranslation();
	const [selectedRole, setSelectedRole] = useState<RoleEnum | null>(null);
	const { navigateWithLanguage } = useLanguageNavigate();
	const { registerWithGoogle, isLoading } = useRegisterWithGoogle();
	const handleSelection = (role: RoleEnum | SpecialistType) => {
		setSelectedRole(role as RoleEnum);
	};
	const { loginWithGoogle } = useLoginWithGoogle();

	const clearStore = useCallback(() => {
		localStorage.removeItem('user');
		localStorage.removeItem('role');
	}, []); // Memoize clearStore

	clearStore();

	const handleNextStep = () => {
		if (selectedRole) {
			localStorage.setItem('role', selectedRole);
			navigateWithLanguage('/register/email-and-password');
		}
	};

	const handleSuccess = useCallback(
		async (credentials: any) => {
			const accessToken = credentials.access_token;

			const response = await registerWithGoogle({
				role: selectedRole as string,
				code: accessToken,
			});

			const decodedToken = decodeToken(response?.access_token || '');

			if (decodedToken && selectedRole === RoleEnum.Specialist) {
				navigateWithLanguage('/specialist-switcher');
			} else {
				await loginWithGoogle(accessToken);
				navigateWithLanguage('/register/farmer/personal-details', {
					state: { step: StepNames.PERSONAL_DETAILS },
				});
			}
			clearStore();
			navigateWithLanguage(`/${selectedRole}/personal-details`);
		},
		[registerWithGoogle, selectedRole, clearStore, navigateWithLanguage, loginWithGoogle]
	);

	const googleLogin = useGoogleLogin({
		onSuccess: handleSuccess,
		flow: 'implicit',
	});

	return (
		<AuthPepper>
			<div className="mt-[50px] max-sm:mt-[80px]">
				<AuthHeader
					title={t('singUp')}
					spacing="mt-[50px] max-sm:mt-[30px]"
					wrapperSpacing="mb-[50px] max-sm:mb-[30px]"
				/>
			</div>
			<div className="flex flex-col justify-center items-center">
				<div className="grid grid-cols-2 place-items-center md:flex md:flex-row justify-center gap-[22px] max-w-full px-4 md:px-0">
					{generalRegistrationRoles.map(({ role, title, description }) => (
						<RoleCard
							key={role}
							role={selectedRole as string}
							title={t(title)}
							description={t(description)}
							selectedRole={role}
							onSelect={() => handleSelection(role)}
						/>
					))}
				</div>
				<div className="flex justify-center flex-col items-center gap-6">
					<div className="mb-4">
						<Button
							disabled={!selectedRole || isLoading}
							className={'w-[340px] !mt-10'}
							onClick={handleNextStep}
							variant="contained"
							classes={{
								root: 'mt-10',
							}}
						>
							{t('next')}
							<KeyboardArrowRightIcon />
						</Button>
						<DividerWithText text={t('or')} widthClass="w-[340px] mt-5" />
						<div className="flex justify-center mt-5 mb-[50px]">
							<div className="w-[340px] relative">
								<GoogleButton
									disabled={!selectedRole || isLoading}
									handleClick={() => googleLogin()}
								/>
								{(!selectedRole || isLoading) && (
									<div className="absolute inset-0 bg-gray-100 opacity-50"></div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</AuthPepper>
	);
};
